import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import type { DialogActionsProps } from "@mui/material";
import { Button, DialogActions, Tooltip } from "@mui/material";
import { Children, Fragment, isValidElement, type ReactNode } from "react";

const normalizeLabel = (value: ReactNode): string => {
  if (typeof value === "string") {
    return value.toLowerCase();
  }
  if (typeof value === "number") {
    return String(value).toLowerCase();
  }
  if (Array.isArray(value)) {
    return value.map(normalizeLabel).join(" ");
  }
  return "";
};

const isEmptyNode = (node: ReactNode): boolean => {
  if (node === null || node === undefined || node === false) {
    return true;
  }
  if (typeof node === "string") {
    return node.trim().length === 0;
  }
  if (Array.isArray(node)) {
    return node.every((item) => isEmptyNode(item));
  }
  if (isValidElement(node) && node.type === Fragment) {
    const props = node.props as { children?: ReactNode };
    return Children.count(props.children) === 0;
  }
  return false;
};

const inferPrimaryIcon = (text: ReactNode): ReactNode => {
  const label = normalizeLabel(text);

  if (label.includes("delete") || label.includes("remove")) {
    return <DeleteOutlineRoundedIcon fontSize="small" />;
  }
  if (
    label.includes("import") ||
    label.includes("sync") ||
    label.includes("retry") ||
    label.includes("refresh")
  ) {
    return <SyncRoundedIcon fontSize="small" />;
  }
  if (label.includes("save") || label.includes("update")) {
    return <SaveRoundedIcon fontSize="small" />;
  }
  if (label.includes("continue") || label.includes("next")) {
    return <ArrowForwardRoundedIcon fontSize="small" />;
  }
  if (label.includes("create") || label.includes("new") || label.includes("add")) {
    return <AddRoundedIcon fontSize="small" />;
  }

  return <CheckRoundedIcon fontSize="small" />;
};

const inferCancelIcon = (text: ReactNode): ReactNode => {
  const label = normalizeLabel(text);
  if (label.includes("back")) {
    return <ArrowBackRoundedIcon fontSize="small" />;
  }
  return <CloseRoundedIcon fontSize="small" />;
};

const StandardDialogActions: React.FC<{
  primaryText: React.ReactNode;
  primaryIcon?: React.ReactNode;
  cancelText?: React.ReactNode;
  cancelIcon?: React.ReactNode;
  primaryColor?:
    | "primary"
    | "secondary"
    | "inherit"
    | "success"
    | "error"
    | "info"
    | "warning";
  onPrimaryClicked?: () => void;
  onCancel: () => void;
  loading: boolean;
  sx?: DialogActionsProps["sx"];
  disabled?: boolean;
  canCancel?: boolean;
  disabledReason?: string;
}> = ({
  primaryText,
  cancelText = "Cancel",
  primaryIcon,
  cancelIcon,
  primaryColor = "primary",
  onPrimaryClicked,
  onCancel,
  loading,
  sx,
  disabled,
  canCancel = true,
  disabledReason,
}) => {
  const resolvedPrimaryIcon = isEmptyNode(primaryIcon)
    ? inferPrimaryIcon(primaryText)
    : primaryIcon;
  const resolvedCancelIcon = isEmptyNode(cancelIcon)
    ? inferCancelIcon(cancelText)
    : cancelIcon;

  const primaryButtonType = onPrimaryClicked === undefined ? "submit" : "button";
  const handlePrimaryClick = onPrimaryClicked ?? (() => void null);

  const primaryButton = (
    <Button
      type={primaryButtonType}
      variant="outlined"
      color={primaryColor}
      sx={{ minWidth: "100px" }}
      onClick={() => handlePrimaryClick()}
      loading={loading}
      startIcon={resolvedPrimaryIcon}
      disabled={disabled}
    >
      {primaryText}
    </Button>
  );

  return (
    <DialogActions sx={sx}>
      {canCancel && (
        <Button
          variant="text"
          color="primary"
          sx={{ marginRight: "16px", minWidth: "100px" }}
          onClick={() => onCancel()}
          disabled={loading}
          startIcon={resolvedCancelIcon}
        >
          {cancelText}
        </Button>
      )}
      {disabled && disabledReason ? (
        <Tooltip title={disabledReason} placement="top" arrow>
          <span>{primaryButton}</span>
        </Tooltip>
      ) : (
        primaryButton
      )}
    </DialogActions>
  );
};

export default StandardDialogActions;
